// Client for the central codes service. Two endpoints:
//  - GET  /apps/:slug   page metadata (copy + screenshots)
//  - POST /codes/claim/:slug   issue a code on tap
//
// We deliberately don't talk to Firestore from the browser anymore —
// dragging in the Firebase Web SDK + dealing with rules + named-DB
// quirks all to read public data was over-engineered. The codes
// service already has a Firestore handle; serving the read through
// it is one extra Go handler and zero browser deps.

import type { AppDoc, ScreenshotDoc, ClaimResponse } from "./types";

const CODES_BASE =
  import.meta.env.VITE_CODES_BASE ?? "https://codes.gntl.app";

export type LoadOutcome =
  | { kind: "ok"; app: AppDoc; screenshots: ScreenshotDoc[] }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

export async function loadApp(slug: string): Promise<LoadOutcome> {
  let res: Response;
  try {
    res = await fetch(`${CODES_BASE}/apps/${encodeURIComponent(slug)}`);
  } catch (e) {
    return { kind: "error", message: e instanceof Error ? e.message : String(e) };
  }
  if (res.status === 404) return { kind: "not_found" };
  if (!res.ok) return { kind: "error", message: `HTTP ${res.status}` };
  const body = (await res.json()) as {
    slug?: string;
    display_name?: string;
    play_package?: string;
    beta_enabled?: boolean;
    beta?: AppDoc["beta"];
    screenshots?: ScreenshotDoc[];
  };
  const app: AppDoc = {
    id: body.slug ?? slug,
    slug: body.slug,
    display_name: body.display_name,
    play_package: body.play_package,
    beta_enabled: body.beta_enabled,
    beta: body.beta,
  };
  return { kind: "ok", app, screenshots: body.screenshots ?? [] };
}

export type ClaimOutcome =
  | { kind: "ok"; code: string }
  | { kind: "disabled" }
  | { kind: "no_codes" }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

export type EnrollOutcome =
  | { kind: "ok" }
  | { kind: "invalid_email" }
  | { kind: "account_not_found" } // gmail typo — Google says no such account
  | { kind: "disabled" }
  | { kind: "not_found" }
  | { kind: "not_configured" }
  | { kind: "enroll_failed" }
  | { kind: "error"; message: string };

// enrollBeta — POST /beta/enroll/:slug with {email}. The backend adds
// the email to the closed-beta Google Group and verifies membership
// before returning, so a 200 from this call means the user is good to
// proceed to the Play install step.
export async function enrollBeta(
  slug: string,
  email: string,
): Promise<EnrollOutcome> {
  let res: Response;
  try {
    res = await fetch(`${CODES_BASE}/beta/enroll/${encodeURIComponent(slug)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch (e) {
    return { kind: "error", message: e instanceof Error ? e.message : String(e) };
  }
  if (res.ok) return { kind: "ok" };
  if (res.status === 400) return { kind: "invalid_email" };
  if (res.status === 422) return { kind: "account_not_found" };
  if (res.status === 403) return { kind: "disabled" };
  if (res.status === 404) return { kind: "not_found" };
  if (res.status === 503) return { kind: "not_configured" };
  if (res.status === 500 || res.status === 502) return { kind: "enroll_failed" };
  return { kind: "error", message: `HTTP ${res.status}` };
}

export async function claimCode(slug: string): Promise<ClaimOutcome> {
  let res: Response;
  try {
    res = await fetch(`${CODES_BASE}/codes/claim/${encodeURIComponent(slug)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  } catch (e) {
    return { kind: "error", message: e instanceof Error ? e.message : String(e) };
  }

  if (res.ok) {
    const body = (await res.json()) as ClaimResponse;
    return { kind: "ok", code: body.code };
  }
  if (res.status === 403) return { kind: "disabled" };
  if (res.status === 410) return { kind: "no_codes" };
  if (res.status === 404) return { kind: "not_found" };
  return { kind: "error", message: `HTTP ${res.status}` };
}
